import type { TextStyle } from 'react-native';
import { FontFamily, getHeadingFont, getBodyFont } from '@/lib/theme/FontManager';

export type TypographyVariant =
  | 'hero'
  | 'title'
  | 'heading'
  | 'subheading'
  | 'body'
  | 'caption';

export type FontScaleCategory = 'smaller' | 'standard' | 'larger';

type TypographyDefinition = {
  fontSize: number;
  lineHeight: number;
  fontWeight?: TextStyle['fontWeight'];
};

type TypographyWithFont = TypographyDefinition & { fontFamily: string };

/**
 * Font family assignments for each typography variant
 * - Comfortaa: hero, title, heading (display/headings)
 * - Noto Sans: subheading, body, caption (readable text)
 */
const VARIANT_FONT_FAMILY: Record<TypographyVariant, string> = {
  hero: FontFamily.heading,
  title: FontFamily.heading,
  heading: FontFamily.heading,
  subheading: FontFamily.body,
  body: FontFamily.body,
  caption: FontFamily.body,
};

/**
 * Premium typography scale - refined sizes for modern feel
 * All sizes reduced by 2px, using only even values
 */
const BASE_TYPOGRAPHY: Record<TypographyVariant, TypographyDefinition> = {
  hero: { fontSize: 26, lineHeight: 32 },
  title: { fontSize: 20, lineHeight: 26 },
  heading: { fontSize: 16, lineHeight: 22 },
  subheading: { fontSize: 14, lineHeight: 20, fontWeight: '600' },
  body: { fontSize: 12, lineHeight: 18, fontWeight: '400' },
  caption: { fontSize: 10, lineHeight: 14, fontWeight: '400' },
};

const CATEGORY_ADJUSTMENTS: Record<FontScaleCategory, number> = {
  smaller: -2,
  standard: 0,
  larger: 2,
};

const MIN_FONT_SIZE = 10;

/**
 * Get font size for a typography variant with optional scaling
 */
export const getFontSize = (
  variant: TypographyVariant,
  category: FontScaleCategory = 'standard'
): number => {
  const base = BASE_TYPOGRAPHY[variant];
  const adjustment = CATEGORY_ADJUSTMENTS[category];

  return Math.max(MIN_FONT_SIZE, base.fontSize + adjustment);
};

/**
 * Get complete typography style including font family
 * Use this when applying styles directly to Text components
 */
export const getTypographyStyle = (
  variant: TypographyVariant,
  category: FontScaleCategory = 'standard'
): TypographyWithFont => {
  const base = BASE_TYPOGRAPHY[variant];
  const adjustment = CATEGORY_ADJUSTMENTS[category];
  const fontSize = Math.max(MIN_FONT_SIZE, base.fontSize + adjustment);
  const lineHeight = Math.max(fontSize, base.lineHeight + adjustment);
  const fontFamily = VARIANT_FONT_FAMILY[variant];

  return {
    ...base,
    fontSize,
    lineHeight,
    fontFamily,
  };
};

/**
 * Get complete typography style including font family
 * @deprecated Use getTypographyStyle instead which now includes font family
 */
export const getTypographyStyleWithFont = getTypographyStyle;

/**
 * Get the font family for a specific variant
 */
export const getFontFamily = (variant: TypographyVariant): string => {
  return VARIANT_FONT_FAMILY[variant];
};

// Re-export font utilities for convenience
export { getHeadingFont, getBodyFont, FontFamily };
